import React from 'react';
import { 
  stemInfo, branchInfo, branchHiddenStems, 
  computeTenGod, getNaYinElement, getQiPhase, qiPhaseMap 
} from '../utils/baziHelper';
import '../App.css';

export interface Pillar {
  position: string;
  stem: string;
  branch: string;
  element?: string;
  pinyin?: string;
  ciongStem?: boolean;
  ciongBranch?: boolean;
}

export interface ChartData {
  chart_id?: string;
  date_of_birth?: string;
  time_of_birth?: string;
  gender?: string;
  pillars?: Pillar[];
  day_master_strength?: string;
  day_master_scores?: { total_score?: number; hour_stem_score?: number; day_stem_score?: number; month_stem_score?: number; year_stem_score?: number };
  pilar_bumi_scores?: { total_score?: number; hour_branch_score?: number; day_branch_score?: number; month_branch_score?: number; year_branch_score?: number };
  career_health?: { details?: { element_pendukung?: string[] } };
  unknown_tob?: boolean;
}

interface Props {
  chart: ChartData;
  unknownTob: boolean;
}

export const BaziChartGrid: React.FC<Props> = ({ chart, unknownTob }) => {
  if (!chart.pillars) return null;

  const hour = chart.pillars.find(p => p.position === 'hour');
  const day = chart.pillars.find(p => p.position === 'day');
  const month = chart.pillars.find(p => p.position === 'month');
  const year = chart.pillars.find(p => p.position === 'year');
  const orderedPillars = [hour, day, month, year];
  const dayMasterStem = day?.stem || '';
  
  const pendukungElements = new Set<string>(chart.career_health?.details?.element_pendukung || []);

  return (
    <div className="bazi-chart-grid" style={{ width: '100%', maxWidth: '100%' }}>
      {/* Row 1: Header */}
      <div className="bazi-chart-header" style={{ borderBottom: '3px solid #1a202c', backgroundColor: '#f7f9fc', fontSize: '0.85rem' }}>
        Pillar
      </div>
      <div className="bazi-chart-header">
        Hour 时
        <div className="bazi-chart-header-sub">{unknownTob ? 'Unknown / ?' : (chart.time_of_birth || '09:00')}</div>
      </div>
      <div className="bazi-chart-header">
        Day 日
        <div className="bazi-chart-header-sub">{chart.date_of_birth ? chart.date_of_birth.split('-')[2] : '15'}</div>
      </div>
      <div className="bazi-chart-header">
        Month 月
        <div className="bazi-chart-header-sub">{chart.date_of_birth ? chart.date_of_birth.split('-')[1] : '05'}</div>
      </div>
      <div className="bazi-chart-header">
        Year 年
        <div className="bazi-chart-header-sub">{chart.date_of_birth ? chart.date_of_birth.split('-')[0] : '1990'}</div>
      </div>

      {/* Row 2: Heavenly Stems (HS) */}
      <div className="bazi-vertical-label-cell" style={{ borderRight: '3px solid #1a202c' }}>
        <div className="vertical-label" style={{ flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 900, color: '#1a202c' }}>HS</span>
          <span style={{ fontSize: '0.65rem', color: '#718096', fontWeight: 800 }}>天干</span>
        </div>
      </div>
      {orderedPillars.map((p, idx) => {
        if (unknownTob && idx === 0) {
          return (
            <div key={`stem-empty-unknown`} className="bazi-cell-stem" style={{ backgroundColor: '#f1f3f5', minHeight: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="bazi-char-large" style={{ color: '#a0aec0', fontSize: '3.5rem', textShadow: 'none' }}>?</div>
              <div className="bazi-char-pinyin" style={{ color: '#a0aec0' }}>Unknown</div>
            </div>
          );
        }
        if (!p) return <div key={`stem-empty-${idx}`} className="bazi-cell-stem" />;
        const stemObj = stemInfo[p.stem];
        const elClass = stemObj ? `bazi-bg-${stemObj.element}` : '';
        const tenGod = dayMasterStem ? computeTenGod(dayMasterStem, p.stem) : null;
        const isDayMaster = idx === 1;
        const isFavorableStem = stemObj && pendukungElements.has(stemObj.element);
        const showCiongStem = Boolean(p.ciongStem && isDayMaster);

        let stemScore: number | undefined;
        if (chart.day_master_scores) {
          if (idx === 0) stemScore = chart.day_master_scores.hour_stem_score;
          if (idx === 1) stemScore = chart.day_master_scores.day_stem_score;
          if (idx === 2) stemScore = chart.day_master_scores.month_stem_score;
          if (idx === 3) stemScore = chart.day_master_scores.year_stem_score;
        }

        return (
          <div key={`stem-${idx}`} className={`bazi-cell-stem ${elClass}`} style={showCiongStem ? { outline: '3px solid #e03131', outlineOffset: '-3px', boxShadow: 'inset 0 0 0 3px #e03131, 0 0 12px 3px rgba(224,49,49,0.6)', position: 'relative' } : (isFavorableStem ? { outline: '3px solid #f59f00', outlineOffset: '-3px', boxShadow: 'inset 0 0 0 3px #f59f00, 0 0 10px 2px rgba(245,159,0,0.45)' } : {})}>
            {showCiongStem && (
              <div style={{ position: 'absolute', top: '4px', right: '4px', backgroundColor: '#e03131', color: '#ffffff', fontSize: '0.6rem', fontWeight: '900', padding: '1px 4px', borderRadius: '4px', textTransform: 'uppercase', lineHeight: 1, zIndex: 10 }}>
                Ciong
              </div>
            )}
            {isDayMaster ? (
              <div className="bazi-stem-tengod" style={{ background: '#ffd43b', borderColor: '#1a202c' }}>
                <div style={{ fontWeight: 900, fontSize: '0.65rem' }}>DM</div>
              </div>
            ) : (
              tenGod && (
                <div className="bazi-stem-tengod">
                  <div className="bazi-tengod-zh-vertical">{tenGod.zhFull}</div>
                  <div className="bazi-tengod-en-label">{tenGod.en}</div>
                </div>
              )
            )}
            <div className="bazi-char-large">{p.stem}</div>
            <div className="bazi-char-pinyin" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              {stemObj?.pinyin || p.stem}
              {stemScore !== undefined && (
                <span style={{ fontSize: '0.65rem', padding: '1px 3px', borderRadius: '3px', border: '1px solid #1a202c', backgroundColor: 'rgba(255,255,255,0.75)', color: '#1a202c', fontWeight: 'bold', lineHeight: 1 }}>
                  {stemScore}
                </span>
              )}
            </div>
            <div className="bazi-char-desc">
              {stemObj ? `${stemObj.polarity} ${stemObj.element.charAt(0).toUpperCase() + stemObj.element.slice(1)}` : ''}
            </div>
          </div>
        );
      })}

      {/* Row 3: Earthly Branches (EB) */}
      <div className="bazi-vertical-label-cell" style={{ borderRight: '3px solid #1a202c' }}>
        <div className="vertical-label" style={{ flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 900, color: '#1a202c' }}>EB</span>
          <span style={{ fontSize: '0.65rem', color: '#718096', fontWeight: 800 }}>地支</span>
        </div>
      </div>
      {orderedPillars.map((p, idx) => {
        if (unknownTob && idx === 0) {
          return (
            <div key={`branch-empty-unknown`} className="bazi-cell-branch" style={{ backgroundColor: '#f1f3f5', minHeight: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="bazi-char-large" style={{ color: '#a0aec0', fontSize: '3.5rem', textShadow: 'none' }}>?</div>
              <div className="bazi-char-pinyin" style={{ color: '#a0aec0' }}>Unknown</div>
            </div>
          );
        }
        if (!p) return <div key={`branch-empty-${idx}`} className="bazi-cell-branch" />;
        const branchObj = branchInfo[p.branch];
        const elClass = branchObj ? `bazi-bg-${branchObj.element}` : '';
        const isFavorableBranch = branchObj && pendukungElements.has(branchObj.element);

        let branchScore: number | undefined;
        if (chart.pilar_bumi_scores) {
          if (idx === 0) branchScore = chart.pilar_bumi_scores.hour_branch_score;
          if (idx === 1) branchScore = chart.pilar_bumi_scores.day_branch_score;
          if (idx === 2) branchScore = chart.pilar_bumi_scores.month_branch_score;
          if (idx === 3) branchScore = chart.pilar_bumi_scores.year_branch_score;
        }

        return (
          <div key={`branch-${idx}`} className={`bazi-cell-branch ${elClass}`} style={p.ciongBranch ? { outline: '3px solid #e03131', outlineOffset: '-3px', boxShadow: 'inset 0 0 0 3px #e03131, 0 0 12px 3px rgba(224,49,49,0.6)', position: 'relative' } : (isFavorableBranch ? { outline: '3px solid #f59f00', outlineOffset: '-3px', boxShadow: 'inset 0 0 0 3px #f59f00, 0 0 10px 2px rgba(245,159,0,0.45)' } : {})}>
            {p.ciongBranch && (
              <div style={{ position: 'absolute', top: '4px', right: '4px', backgroundColor: '#e03131', color: '#ffffff', fontSize: '0.6rem', fontWeight: '900', padding: '1px 4px', borderRadius: '4px', textTransform: 'uppercase', lineHeight: 1, zIndex: 10 }}>
                Ciong
              </div>
            )}
            <div className="bazi-char-large">{p.branch}</div>
            <div className="bazi-char-pinyin" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              {branchObj ? `${branchObj.pinyin} (${branchObj.animal})` : p.branch}
              {branchScore !== undefined && (
                <span style={{ fontSize: '0.65rem', padding: '1px 3px', borderRadius: '3px', border: '1px solid #1a202c', backgroundColor: 'rgba(255,255,255,0.75)', color: '#1a202c', fontWeight: 'bold', lineHeight: 1 }}>
                  {branchScore}
                </span>
              )}
            </div>
            <div className="bazi-char-desc">
              {branchObj ? `${branchObj.polarity} ${branchObj.element.charAt(0).toUpperCase() + branchObj.element.slice(1)}` : ''}
            </div>
          </div>
        );
      })}

      {/* Row 4: Hidden Heavenly Stems (HHS) */}
      <div className="bazi-vertical-label-cell" style={{ borderRight: '3px solid #1a202c' }}>
        <div className="vertical-label" style={{ flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 900, color: '#1a202c' }}>HHS</span>
          <span style={{ fontSize: '0.65rem', color: '#718096', fontWeight: 800 }}>藏干</span>
        </div>
      </div>
      {orderedPillars.map((p, idx) => {
        if (unknownTob && idx === 0) {
          return (
            <div key={`hidden-empty-unknown`} className="bazi-cell-hidden" style={{ backgroundColor: '#f1f3f5', minHeight: '110px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ color: '#a0aec0', fontWeight: 800, fontSize: '1.2rem' }}>?</div>
            </div>
          );
        }
        if (!p) return <div key={`hidden-empty-${idx}`} className="bazi-cell-hidden" />;
        const hiddenStems = branchHiddenStems[p.branch] || [];
        return (
          <div key={`hidden-${idx}`} className="bazi-cell-hidden" style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px 4px' }}>
            {hiddenStems.map((hs, hsIdx) => {
              const hsStemObj = stemInfo[hs];
              const hsTenGod = dayMasterStem ? computeTenGod(dayMasterStem, hs) : { zhFull: '', zhAbbr: '', en: '' };
              return (
                <div 
                  key={hsIdx} 
                  className={`bazi-hhs-block bazi-bg-${hsStemObj?.element || 'wood'}`}
                  style={{ 
                    display: 'flex', 
                    flexDirection: 'row', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    width: '90%',
                    padding: '2px 6px',
                    borderRadius: '6px',
                    border: '1.5px solid #1a202c',
                    boxShadow: '1.5px 1.5px 0px 0px #1a202c',
                    fontSize: '0.75rem',
                    fontWeight: 800
                  }}
                >
                  <span>{hsStemObj?.pinyin || hs}</span>
                  <span style={{ background: 'rgba(255,255,255,0.6)', padding: '1px 3px', borderRadius: '3px', fontSize: '0.65rem', border: '1px solid #1a202c' }}>{hsTenGod.en}</span>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Row 5: Na Yin */}
      <div className="bazi-vertical-label-cell" style={{ borderRight: '3px solid #1a202c' }}>
        <div className="vertical-label" style={{ flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '0.9rem', fontWeight: 900, color: '#1a202c', textAlign: 'center' }}>Na Yin</span>
          <span style={{ fontSize: '0.65rem', color: '#718096', fontWeight: 800 }}>纳音</span>
        </div>
      </div>
      {orderedPillars.map((p, idx) => {
        if (unknownTob && idx === 0) {
          return (
            <div key={`nayin-empty-unknown`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60px', backgroundColor: '#f1f3f5', borderBottom: '3px solid #1a202c', fontWeight: 800, fontSize: '1.2rem', color: '#a0aec0' }}>?</div>
          );
        }
        if (!p) return <div key={`nayin-empty-${idx}`} style={{ backgroundColor: '#ffffff', minHeight: '60px' }} />;
        const naYin = getNaYinElement(p.stem, p.branch);
        return (
          <div 
            key={`nayin-${idx}`} 
            className={`bazi-bg-${naYin.element}`}
            style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              minHeight: '60px',
              padding: '6px 4px',
              borderBottom: '3px solid #1a202c',
              fontWeight: 800,
              fontSize: '0.8rem',
              color: '#1a202c',
              textAlign: 'center'
            }}
          >
            <div>{naYin.name}</div>
            <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>({naYin.element.toUpperCase()})</div>
          </div>
        );
      })}

      {/* Row 6: DM's qi phase */}
      <div className="bazi-vertical-label-cell" style={{ borderRight: '3px solid #1a202c' }}>
        <div className="vertical-label" style={{ flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 900, color: '#1a202c', textAlign: 'center', lineHeight: 1.1 }}>DM's qi phase</span>
          <span style={{ fontSize: '0.65rem', color: '#718096', fontWeight: 800 }}>十二运</span>
        </div>
      </div>
      {orderedPillars.map((p, idx) => {
        if (unknownTob && idx === 0) {
          return (
            <div key={`qi-empty-unknown`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60px', backgroundColor: '#f1f3f5', fontWeight: 800, fontSize: '1.2rem', color: '#a0aec0' }}>?</div>
          );
        }
        if (!p) return <div key={`qi-empty-${idx}`} style={{ backgroundColor: '#ffffff', minHeight: '60px' }} />;
        const phase = getQiPhase(dayMasterStem, p.branch);
        const detail = qiPhaseMap[phase] || { zh: phase, id: phase };
        return (
          <div 
            key={`qi-${idx}`} 
            style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: '60px',
              padding: '6px 4px',
              backgroundColor: '#f8f9fa',
              color: '#495057',
              fontWeight: 800,
              fontSize: '0.8rem'
            }}
          >
            <div>{detail.zh}</div>
            <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>{detail.id.split(' ')[0]}</div>
          </div>
        );
      })}
    </div>
  );
};
